import React, { FC } from 'react';
import Badge from '@atlaskit/badge';
import { DynamicTableStateless as DynamicTable } from '@atlaskit/dynamic-table';
import { css } from '@emotion/core';
import { SlideDown } from 'react-slidedown';
import 'react-slidedown/lib/slidedown.css';
import { token } from '@atlaskit/tokens';
import ChevronUpIcon from '@atlaskit/icon/glyph/chevron-up';
import pluralize from 'pluralize';

import {
  alignItemsCenter,
  BREACH_COLOR,
  BURN_COLOR,
  INCIDENT_COLOR,
  flex,
  grid,
  spaceGapMinimum,
  SPACE_COMPACT,
  SPACE_COZY,
  threeColumnGrid,
  eventTableStyles,
  BREACH_LIGHT_COLOR,
  BURN_LIGHT_COLOR,
  HISTORICAL_ALERT_COLOR,
  HISTORICAL_LIGHT_ALERT_COLOR,
  CTD_ERROR_RATE_COLOR,
  LOW_TRAFFIC_ALERT_COLOR,
} from '@/styles';
import { fields as fieldConsts } from '@/components/primitive/editable-report/constants';
import { formatDateForDisplay } from '@/utils';
import { DateDisplayFormats } from '@/constants';
import { DataPointsEventsTotals, InterimResults } from '@/ui';
import { selectedDateLabels } from '@/utils/date';
import { localNumberFormat } from '@/utils/string';
import { Incidents } from '@/components/composed';
import { FCC } from '@/utils/types';
import { ItemHeader } from '@/components/primitive';

import {
  formatErrorBudget,
  formatPerformanceResult,
  getDailyPerformance,
  getEventTypes,
} from './slo-card/utils';
import EventAnnotations from './event-annotations';
import { getBurnEventType } from './new-slo-views/common/utils';
import EventsBadge from './slo-card/graph/tooltip/events-badge';
import { GraphEventSource } from './new-slo-views/expanded/graph/graph-tabs';

type InfoPanelProps = {
  open?: boolean;
  payload: DataPointsEventsTotals & { rollup: number; interimResults: InterimResults };
  onClose: () => void;
  graphEventSource?: GraphEventSource;
};

const RightAlign: FCC = props => (
  <div
    css={css`
      text-align: right;
      margin-left: ${SPACE_COZY}px;
    `}
    {...props}
  />
);

const dateColumnStyles = css`
  white-space: nowrap;
`;

const tooltipTableStyles = [
  flex,
  alignItemsCenter,
  spaceGapMinimum,
  css`
    strong {
      font-weight: 400;
    }
  `,
];

type EventTableProps = {
  events: any[];
  fields: any[];
  backgroundColor: string;
  captionLabel: string;
  useWithSfxIcon?: boolean;
  useLowTrafficTag?: boolean;
  isBold?: boolean;
  textColor?: string;
};

// Render an events table for one of the breach/burn types
const EventTable: FC<EventTableProps> = ({
  events,
  fields,
  backgroundColor,
  captionLabel,
  useWithSfxIcon = false,
  textColor = token('color.text.inverse', 'white'),
  useLowTrafficTag = false,
  isBold = false,
}: EventTableProps) => (
  <span css={eventTableStyles}>
    <DynamicTable
      caption={
        <div css={tooltipTableStyles}>
          <EventsBadge
            count={events.length}
            backgroundColor={backgroundColor}
            textColor={textColor}
            caption={captionLabel}
            useWithSfxIcon={useWithSfxIcon}
            useLowTrafficTag={useLowTrafficTag}
            showLowTrafficTooltip
            isBold={isBold}
          />
        </div>
      }
      head={{ cells: fields }}
      rows={events.map(row => ({
        cells: fields.map(({ key }) => {
          if (key === 'type') {
            return {
              key: row[key],
              content: getBurnEventType(row),
            };
          }

          if (key === 'annotation') {
            return {
              key,
              content: <EventAnnotations event={row} />,
            };
          }

          const date = row[key];
          return {
            key: date?.getTime(),
            content: date ? (
              <span css={dateColumnStyles}>
                {formatDateForDisplay(date, DateDisplayFormats.TIME_WITH_NUMERIC_DATE)}
              </span>
            ) : (
              'Ongoing'
            ),
          };
        }),
      }))}
    />
  </span>
);

export const InfoPanel: FC<InfoPanelProps> = ({ open, payload, onClose, graphEventSource }) => {
  const {
    timestamp,
    goodCount,
    badCount,
    rollup,
    result,
    dailyGood = 0,
    dailyBad = 0,
    events: allEvents,
    incidents = [],
    interimResults,
  } = payload;
  const { timestampLabel, rangeLabel } = interimResults
    ? selectedDateLabels(new Date(interimResults.timestamp ?? timestamp), true)
    : selectedDateLabels(timestamp ? new Date(timestamp) : new Date());

  const {
    breachEvents,
    burnEvents,
    sfxBreachEvents,
    sfxBurnEvents,
    lowTrafficBreachEvents,
    lowTrafficBurnEvents,
    lowTrafficSFxBreachEvents,
    lowTrafficSFxBurnEvents,
    sfxCtdErrorRateEvents,
    sfxLowTrafficAlertEvents,
  } = getEventTypes(allEvents, graphEventSource);

  const dailyPerformance = getDailyPerformance(dailyGood, dailyBad);

  const infoPanelResults = css`
    display: grid;
    grid-template-columns: 1fr min-content;
    align-items: baseline;
    gap: ${SPACE_COMPACT}px;
    margin-top: ${SPACE_COMPACT}px;

    & h6 {
      margin: 0;
    }
  `;

  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
  const boxShadow = css`
    box-shadow: 0 1px 3px rgb(0 0 0 / 0.2);
  `;
  const padding = css`
    padding: 0 50px 20px 50px;
  `;

  const centerIcon = css`
    display: block;
    margin-left: auto;
    margin-right: auto;
    width: fit-content;
  `;

  const panelStyles = css`
    margin-top: 40px;
  `;

  const fields = fieldConsts;
  const incidentsCount: number = incidents.length;
  const showHistoricalEvents = graphEventSource === GraphEventSource.Historical;

  return (
    <SlideDown className="info-panel-slidedown">
      {open && (
        <div css={[boxShadow, padding, panelStyles]}>
          <div css={[centerIcon]} onClick={onClose}>
            <ChevronUpIcon label="Close panel" size="large" />
          </div>
          <div css={[grid, threeColumnGrid]}>
            <div>
              <h3>{timestampLabel}</h3>
              <div css={[infoPanelResults]}>
                <ItemHeader>Performance</ItemHeader>
                <RightAlign>{formatPerformanceResult(dailyPerformance)}%</RightAlign>
                <ItemHeader>Good Events</ItemHeader>
                <RightAlign>{localNumberFormat(dailyGood)}</RightAlign>
                <ItemHeader>Bad Events</ItemHeader>
                <RightAlign>{localNumberFormat(dailyBad)}</RightAlign>
              </div>
            </div>
            <div />
            <div>
              <h3>{rangeLabel}</h3>
              <div css={[infoPanelResults]}>
                <ItemHeader>Performance</ItemHeader>
                <RightAlign>{formatPerformanceResult(rollup)}%</RightAlign>
                <ItemHeader>Error Budget</ItemHeader>
                <RightAlign>{formatErrorBudget(result, { truncate: false })}%</RightAlign>
                <ItemHeader>Good Events</ItemHeader>
                <RightAlign>{localNumberFormat(goodCount)}</RightAlign>
                <ItemHeader>Bad Events</ItemHeader>
                <RightAlign>{localNumberFormat(badCount)}</RightAlign>
              </div>
            </div>
          </div>
          {incidentsCount > 0 && (
            <Incidents
              caption={
                <div css={tooltipTableStyles}>
                  <Badge
                    style={{
                      backgroundColor: INCIDENT_COLOR,
                      color: token('color.text.inverse', 'white'),
                    }}
                  >
                    {incidentsCount}
                  </Badge>
                  <span>Associated {pluralize('Incident', incidentsCount)}</span>
                  <span>{String.fromCodePoint(0x1f525)}</span>
                </div>
              }
              isLoading={false}
              data={incidents}
            />
          )}
          {!!breachEvents.length && (
            <EventTable
              events={breachEvents}
              fields={fields.breaches}
              backgroundColor={showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BREACH_COLOR}
              isBold
              captionLabel={`SLO ${pluralize('Breach', breachEvents.length)}`}
            />
          )}
          {!!lowTrafficBreachEvents.length && (
            <EventTable
              events={lowTrafficBreachEvents}
              fields={fields.breaches}
              backgroundColor={
                showHistoricalEvents ? HISTORICAL_LIGHT_ALERT_COLOR : BREACH_LIGHT_COLOR
              }
              textColor={showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BREACH_COLOR}
              captionLabel={`SLO ${pluralize('Breach', lowTrafficBreachEvents.length)}`}
              useLowTrafficTag
            />
          )}
          {!!burnEvents.length && (
            <EventTable
              events={burnEvents}
              fields={fields.burnRateAlerts}
              backgroundColor={showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BURN_COLOR}
              isBold
              captionLabel={`Error Budget ${pluralize('Burn', burnEvents.length)}`}
            />
          )}
          {!!lowTrafficBurnEvents.length && (
            <EventTable
              events={lowTrafficBurnEvents}
              fields={fields.burnRateAlerts}
              backgroundColor={
                showHistoricalEvents ? HISTORICAL_LIGHT_ALERT_COLOR : BURN_LIGHT_COLOR
              }
              textColor={showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BURN_COLOR}
              captionLabel={`Error Budget ${pluralize('Burn', lowTrafficBurnEvents.length)}`}
              useLowTrafficTag
            />
          )}
          {!!sfxBreachEvents.length && (
            <EventTable
              events={sfxBreachEvents}
              fields={fields.sfxBreaches}
              backgroundColor={showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BREACH_COLOR}
              isBold
              captionLabel={`Breach ${pluralize('Alert', sfxBreachEvents.length)}`}
            />
          )}
          {!!lowTrafficSFxBreachEvents.length && (
            <EventTable
              events={lowTrafficSFxBreachEvents}
              fields={fields.sfxBreaches}
              backgroundColor={
                showHistoricalEvents ? HISTORICAL_LIGHT_ALERT_COLOR : BREACH_LIGHT_COLOR
              }
              textColor={showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BREACH_COLOR}
              captionLabel={`Breach ${pluralize('Alert', lowTrafficSFxBreachEvents.length)}`}
              useLowTrafficTag
            />
          )}
          {!!sfxBurnEvents.length && (
            <EventTable
              events={sfxBurnEvents}
              fields={fields.sfxBurnsExtended}
              backgroundColor={showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BURN_COLOR}
              isBold
              captionLabel={`Burn ${pluralize('Alert', sfxBurnEvents.length)}`}
            />
          )}
          {!!lowTrafficSFxBurnEvents.length && (
            <EventTable
              events={lowTrafficSFxBurnEvents}
              fields={fields.sfxBurnsExtended}
              backgroundColor={
                showHistoricalEvents ? HISTORICAL_LIGHT_ALERT_COLOR : BURN_LIGHT_COLOR
              }
              textColor={showHistoricalEvents ? HISTORICAL_ALERT_COLOR : BURN_COLOR}
              captionLabel={`Burn ${pluralize('Alert', lowTrafficSFxBurnEvents.length)}`}
              useLowTrafficTag
            />
          )}
          {!!sfxCtdErrorRateEvents.length && (
            <EventTable
              events={sfxCtdErrorRateEvents}
              fields={fields.sfxCtdErrorRates}
              backgroundColor={showHistoricalEvents ? HISTORICAL_ALERT_COLOR : CTD_ERROR_RATE_COLOR}
              isBold
              captionLabel={`Consecutive Trial Detector ${pluralize(
                'Alert',
                sfxCtdErrorRateEvents.length
              )}`}
            />
          )}
          {!!sfxLowTrafficAlertEvents.length && (
            <EventTable
              events={sfxLowTrafficAlertEvents}
              fields={fields.sfxLowTrafficAlerts}
              backgroundColor={
                showHistoricalEvents ? HISTORICAL_ALERT_COLOR : LOW_TRAFFIC_ALERT_COLOR
              }
              isBold
              captionLabel={`Low Traffic ${pluralize('Alert', sfxLowTrafficAlertEvents.length)}`}
            />
          )}
        </div>
      )}
    </SlideDown>
  );
};
